import React, { useEffect, useRef, useState } from 'react';
import { CalendarClock, Copy, CreditCard, ExternalLink, MessageCircleMore, Save, Settings, ShieldCheck, Truck } from 'lucide-react';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/mockData';
import { useAuthStore } from '../../store/authStore';
import { resolveAssetUrl } from '../../lib/assets';
import type { DeliveryZone } from '../../types';
import DeliverySettingsSection from './settings/DeliverySettingsSection';
import PaymentsSettingsSection from './settings/PaymentsSettingsSection';
import ReservationsSettingsSection from './settings/ReservationsSettingsSection';
import TablesSettingsSection from './settings/TablesSettingsSection';
import WhatsAppSettingsSection from './settings/WhatsAppSettingsSection';

const defaultDeliveryPaymentMethods = ['MERCADO_PAGO', 'CARD_ON_DELIVERY', 'CASH'];

const subscriptionLabel: Record<string, string> = {
  BASIC: 'Basic',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

const statusLabel: Record<string, string> = {
  TRIALING: 'Em trial',
  ACTIVE: 'Ativa',
  PAST_DUE: 'Pagamento pendente',
  CANCELED: 'Cancelada',
};

const cycleLabel: Record<string, string> = {
  MONTHLY: 'Mensal',
  YEARLY: 'Anual',
};

const defaultOperatingHours = {
  sunday: { open: '11:00', close: '22:00', closed: false },
  monday: { open: '11:00', close: '22:00', closed: false },
  tuesday: { open: '11:00', close: '22:00', closed: false },
  wednesday: { open: '11:00', close: '22:00', closed: false },
  thursday: { open: '11:00', close: '22:00', closed: false },
  friday: { open: '11:00', close: '23:00', closed: false },
  saturday: { open: '11:00', close: '23:00', closed: false },
};

const calculateDistanceInKm = (fromLat: number, fromLng: number, toLat: number, toLng: number) => {
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const deltaLat = toRadians(toLat - fromLat);
  const deltaLng = toRadians(toLng - fromLng);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const AdminSettings: React.FC = () => {
  const { user } = useAuthStore();
  const [form, setForm] = useState({
    name: '',
    slug: '',
    address: '',
    phone: '',
    logoUrl: '',
    coverImageUrl: '',
    coverImageKey: '',
    shareImageUrl: '',
    shareImageKey: '',
    tagline: '',
    primaryColor: '#ff6b00',
    accentColor: '#ffb26b',
    storeThemePreset: 'classic',
    currency: 'BRL',
    theme: 'dark',
      deliveryPaymentMethods: defaultDeliveryPaymentMethods,
      deliveryEnabled: true,
      pickupEnabled: true,
      deliveryFixedFee: '0',
      deliveryMapCenterLat: '-19.9167',
      deliveryMapCenterLng: '-43.9345',
      hasReservations: true,
    reservationSlotMinutes: '30',
    maxReservationsPerSlot: '4',
    maxGuestsPerSlot: '16',
    operatingHours: defaultOperatingHours,
  });
  const [subscription, setSubscription] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentTesting, setPaymentTesting] = useState(false);
  const [billingFeedback, setBillingFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectingBilling, setRedirectingBilling] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [shareImageFile, setShareImageFile] = useState<File | null>(null);
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const [tables, setTables] = useState<Array<{ id: string; label: string; capacity: number; active: boolean }>>([]);
  const [tableDraft, setTableDraft] = useState({ label: '', capacity: '4' });
  const [tableSaving, setTableSaving] = useState(false);
  const [deliveryZones, setDeliveryZones] = useState<DeliveryZone[]>([]);
  const [persistedDeliveryZones, setPersistedDeliveryZones] = useState<DeliveryZone[]>([]);
  const [deliveryZoneSaving, setDeliveryZoneSaving] = useState(false);
  const [deviceLocationLoading, setDeviceLocationLoading] = useState(false);
  const [addressLocationLoading, setAddressLocationLoading] = useState(false);
  const [deviceLocationError, setDeviceLocationError] = useState<string | null>(null);
  const [deliveryZoneFeedback, setDeliveryZoneFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [reservationModuleEnabled, setReservationModuleEnabled] = useState(true);
  const [tableModuleEnabled, setTableModuleEnabled] = useState(true);
  const [mercadoPagoForm, setMercadoPagoForm] = useState({
    mpPublicKey: '',
    mpAccessToken: '',
    hasAccessToken: false,
    updatedAt: '',
  });
  const [paymentFeedback, setPaymentFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const embeddedSignupPayloadRef = useRef<{
    phoneNumberId?: string;
    wabaId?: string;
    businessAccountId?: string;
    businessName?: string;
    displayPhoneNumber?: string;
  }>({});
  const whatsAppOauthStateRef = useRef('');
  const [whatsAppPlatformConfig, setWhatsAppPlatformConfig] = useState<any>(null);
  const [whatsAppIntegration, setWhatsAppIntegration] = useState<any>(null);
  const [whatsAppLoading, setWhatsAppLoading] = useState(true);
  const [whatsAppConnecting, setWhatsAppConnecting] = useState(false);
  const [whatsAppDisconnecting, setWhatsAppDisconnecting] = useState(false);
  const [whatsAppTesting, setWhatsAppTesting] = useState(false);
  const [whatsAppInboxLoading, setWhatsAppInboxLoading] = useState(false);
  const [whatsAppTestForm, setWhatsAppTestForm] = useState({
    to: '',
    text: 'Mensagem de teste enviada pelo Garçom Digital via WhatsApp Business Cloud API.',
  });
  const [whatsAppMessageLogs, setWhatsAppMessageLogs] = useState<any[]>([]);
  const [whatsAppFeedback, setWhatsAppFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!user?.establishmentId) return;

    Promise.all([
      api.get(`/establishments/id/${user.establishmentId}`),
      api.get(`/establishments/${user.establishmentId}/payment-settings/mercado-pago`),
      api.get('/whatsapp/platform/config'),
      api.get('/whatsapp/integration'),
    ])
      .then(([establishmentResponse, paymentSettingsResponse, whatsAppPlatformConfigResponse, whatsAppIntegrationResponse]) => {
        const establishment = establishmentResponse.data;
        const paymentSettings = paymentSettingsResponse.data;

        setForm({
          name: establishment.name ?? '',
          slug: establishment.slug ?? '',
          address: establishment.address ?? '',
          phone: establishment.phone ?? '',
          logoUrl: establishment.logoUrl ?? '',
          coverImageUrl: establishment.settings?.coverImageUrl ?? '',
          coverImageKey: establishment.settings?.coverImageKey ?? '',
          shareImageUrl: establishment.settings?.shareImageUrl ?? '',
          shareImageKey: establishment.settings?.shareImageKey ?? '',
          tagline: establishment.settings?.tagline ?? '',
          primaryColor: establishment.settings?.primaryColor ?? '#ff6b00',
          accentColor: establishment.settings?.accentColor ?? '#ffb26b',
          storeThemePreset: establishment.settings?.storeThemePreset ?? 'classic',
          currency: establishment.settings?.currency ?? 'BRL',
          theme: establishment.settings?.theme ?? 'dark',
          deliveryPaymentMethods: establishment.settings?.deliveryPaymentMethods ?? defaultDeliveryPaymentMethods,
          deliveryEnabled: establishment.settings?.deliveryEnabled !== false,
          pickupEnabled: establishment.settings?.pickupEnabled !== false,
          deliveryFixedFee: String(establishment.settings?.deliveryFixedFee ?? 0),
          deliveryMapCenterLat: String(establishment.settings?.deliveryMapCenterLat ?? -19.9167),
          deliveryMapCenterLng: String(establishment.settings?.deliveryMapCenterLng ?? -43.9345),
          hasReservations: establishment.settings?.hasReservations !== false,
          reservationSlotMinutes: String(establishment.settings?.reservationSlotMinutes ?? 30),
          maxReservationsPerSlot: String(establishment.settings?.maxReservationsPerSlot ?? 4),
          maxGuestsPerSlot: String(establishment.settings?.maxGuestsPerSlot ?? 16),
          operatingHours: establishment.settings?.operatingHours ?? defaultOperatingHours,
        });
        setReservationModuleEnabled(establishment.settings?.hasReservationModule !== false);
        setTableModuleEnabled(establishment.settings?.hasTableModule !== false);
        setTables(establishment.tables ?? []);
        setDeliveryZones(establishment.deliveryZones ?? []);
        setPersistedDeliveryZones(establishment.deliveryZones ?? []);
        setSubscription({
          subscriptionPlan: establishment.subscriptionPlan,
          subscriptionStatus: establishment.subscriptionStatus,
          billingCycle: establishment.billingCycle,
          subscriptionPrice: establishment.subscriptionPrice,
          trialEndsAt: establishment.trialEndsAt,
          nextBillingAt: establishment.nextBillingAt,
        });
        setMercadoPagoForm({
          mpPublicKey: paymentSettings.mpPublicKey ?? '',
          mpAccessToken: '',
          hasAccessToken: !!paymentSettings.hasAccessToken,
          updatedAt: paymentSettings.updatedAt ?? '',
        });
        setWhatsAppPlatformConfig(whatsAppPlatformConfigResponse.data);
        setWhatsAppIntegration(whatsAppIntegrationResponse.data);
      })
      .catch((error) => console.error('Failed to load settings', error))
      .finally(() => {
        setLoading(false);
        setWhatsAppLoading(false);
      });
  }, [user]);

  useEffect(() => {
    if (!user?.establishmentId || !whatsAppIntegration?.connected) {
      setWhatsAppMessageLogs([]);
      return;
    }

    let active = true;

    const loadLogs = async (showLoader = false) => {
      try {
        if (showLoader) setWhatsAppInboxLoading(true);
        const { data } = await api.get('/whatsapp/message-logs?limit=12');
        if (active) {
          setWhatsAppMessageLogs(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to load WhatsApp message logs', error);
      } finally {
        if (active && showLoader) {
          setWhatsAppInboxLoading(false);
        }
      }
    };

    void loadLogs(true);
    const interval = window.setInterval(() => void loadLogs(false), 10000);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [user, whatsAppIntegration?.connected]);

  useEffect(() => {
    const handleEmbeddedSignupMessage = (event: MessageEvent) => {
      let hostname = '';
      try {
        hostname = new URL(event.origin).hostname;
      } catch {
        return;
      }

      if (!/facebook\.com$/.test(hostname)) {
        return;
      }

      let payload = event.data;

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      const eventName = payload?.event || payload?.type;
      const data = payload?.data || payload;

      if (!data || (eventName !== 'FINISH' && eventName !== 'WA_EMBEDDED_SIGNUP' && eventName !== 'WA_EMBEDDED_SIGNUP_FINISH')) {
        return;
      }

      embeddedSignupPayloadRef.current = {
        phoneNumberId: data.phone_number_id || data.phoneNumberId,
        wabaId: data.waba_id || data.wabaId,
        businessAccountId: data.business_account_id || data.businessAccountId,
        businessName: data.business_name || data.businessName,
        displayPhoneNumber: data.display_phone_number || data.displayPhoneNumber,
      };
    };

    window.addEventListener('message', handleEmbeddedSignupMessage);
    return () => window.removeEventListener('message', handleEmbeddedSignupMessage);
  }, []);

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateBooleanField = (field: 'hasReservations', value: boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateToggleField = (field: 'deliveryEnabled' | 'pickupEnabled', value: boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateOperatingHour = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day as keyof typeof prev.operatingHours],
          [field]: value,
        },
      },
    }));
  };

  const toggleDeliveryPaymentMethod = (method: string) => {
    setForm((prev) => ({
      ...prev,
      deliveryPaymentMethods: prev.deliveryPaymentMethods.includes(method)
        ? prev.deliveryPaymentMethods.filter((item) => item !== method)
        : [...prev.deliveryPaymentMethods, method],
    }));
  };

  const updateDeliveryZone = (zoneId: string, updates: Partial<DeliveryZone>) => {
    setDeliveryZones((prev) => prev.map((zone) => (zone.id === zoneId ? { ...zone, ...updates } : zone)));
    setDeliveryZoneFeedback(null);
  };

  const updateMercadoPagoField = (field: 'mpPublicKey' | 'mpAccessToken', value: string) => {
    setMercadoPagoForm((prev) => ({ ...prev, [field]: value }));
    setPaymentFeedback(null);
  };

  const useDeviceLocationForDeliveryMap = () => {
    if (!navigator.geolocation) {
      setDeviceLocationError('Este navegador nao suporta geolocalizacao.');
      return;
    }

    setDeviceLocationLoading(true);
    setDeviceLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLat = Number(position.coords.latitude.toFixed(6));
        const nextLng = Number(position.coords.longitude.toFixed(6));
        const currentLat = Number(form.deliveryMapCenterLat);
        const currentLng = Number(form.deliveryMapCenterLng);
        const distanceFromCurrentKm =
          Number.isFinite(currentLat) && Number.isFinite(currentLng)
            ? calculateDistanceInKm(currentLat, currentLng, nextLat, nextLng)
            : 0;

        if (position.coords.accuracy > 5000) {
          setDeviceLocationError(
            `A localizacao encontrada esta muito imprecisa (${Math.round(position.coords.accuracy)} m). Ative a localizacao precisa do dispositivo e tente novamente.`,
          );
          setDeviceLocationLoading(false);
          return;
        }

        if (distanceFromCurrentKm > 100) {
          const shouldApply = window.confirm(
            `A localizacao encontrada esta a aproximadamente ${Math.round(distanceFromCurrentKm)} km do centro atual do mapa. Deseja usar esse ponto mesmo assim?`,
          );

          if (!shouldApply) {
            setDeviceLocationLoading(false);
            return;
          }
        }

        updateField('deliveryMapCenterLat', nextLat.toFixed(6));
        updateField('deliveryMapCenterLng', nextLng.toFixed(6));
        setDeviceLocationLoading(false);
      },
      (error) => {
        const messageByCode: Record<number, string> = {
          [error.PERMISSION_DENIED]: 'Voce negou o acesso a localizacao do dispositivo.',
          [error.POSITION_UNAVAILABLE]: 'Nao foi possivel obter a localizacao atual.',
          [error.TIMEOUT]: 'A busca pela localizacao demorou demais. Tente novamente.',
        };

        setDeviceLocationError(messageByCode[error.code] || 'Nao foi possivel usar a localizacao do dispositivo.');
        setDeviceLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const centerDeliveryMapFromRestaurantAddress = async () => {
    if (!user?.establishmentId) return;

    if (!form.address.trim()) {
      setDeviceLocationError('Preencha o endereco do restaurante antes de centralizar pelo endereco.');
      return;
    }

    try {
      setAddressLocationLoading(true);
      setDeviceLocationError(null);

      const { data } = await api.post(`/establishments/${user.establishmentId}/check-delivery-area`, {
        address: form.address,
      });

      if (!Number.isFinite(data?.lat) || !Number.isFinite(data?.lng)) {
        throw new Error('Nao foi possivel obter coordenadas para esse endereco.');
      }

      updateField('deliveryMapCenterLat', Number(data.lat).toFixed(6));
      updateField('deliveryMapCenterLng', Number(data.lng).toFixed(6));
    } catch (error: any) {
      setDeviceLocationError(
        error?.response?.data?.message || error?.message || 'Nao foi possivel centralizar o mapa pelo endereco do restaurante.',
      );
    } finally {
      setAddressLocationLoading(false);
    }
  };

  const buildWhatsAppOAuthUrl = () => {
    const params = new URLSearchParams({
      client_id: whatsAppPlatformConfig.appId,
      config_id: whatsAppPlatformConfig.configId,
      response_type: 'code',
      redirect_uri: whatsAppPlatformConfig.redirectUri,
      scope: whatsAppPlatformConfig.scopes?.join(',') || 'business_management,whatsapp_business_management,whatsapp_business_messaging',
      state: whatsAppOauthStateRef.current,
    });

    return `https://www.facebook.com/${whatsAppPlatformConfig.graphVersion || 'v22.0'}/dialog/oauth?${params.toString()}`;
  };

  const openWhatsAppEmbeddedSignup = async () => {
    if (!whatsAppPlatformConfig?.enabled || !whatsAppPlatformConfig?.appId || !whatsAppPlatformConfig?.configId || !whatsAppPlatformConfig?.redirectUri) {
      setWhatsAppFeedback({
        type: 'error',
        message: 'Configuracao da Meta incompleta. Verifique appId, configId e redirectUri.',
      });
      return;
    }

    setWhatsAppConnecting(true);
    setWhatsAppFeedback(null);
    embeddedSignupPayloadRef.current = {};
    whatsAppOauthStateRef.current = `wa_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    try {
      await new Promise<void>((resolve, reject) => {
        const popup = window.open(
          buildWhatsAppOAuthUrl(),
          'gastroflow-whatsapp-embedded-signup',
          'width=720,height=820,noopener=no,noreferrer=no',
        );

        if (!popup) {
          reject(new Error('O navegador bloqueou a janela do Embedded Signup.'));
          return;
        }

        const cleanup = () => {
          window.removeEventListener('message', handleOAuthMessage);
          clearInterval(closeWatcher);
        };

        const handleOAuthMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data?.source !== 'gastroflow-whatsapp-oauth') {
            return;
          }

          cleanup();

          if (event.data?.error) {
            reject(new Error(event.data.error));
            return;
          }

          if (!event.data?.code) {
            reject(new Error('O Embedded Signup nao retornou um code de autorizacao.'));
            return;
          }

          const embeddedData = embeddedSignupPayloadRef.current;
          void (async () => {
            try {
              const payload: Record<string, string> = {
                code: event.data.code,
                tokenType: 'Bearer',
              };

              if (embeddedData.phoneNumberId) payload.phoneNumberId = embeddedData.phoneNumberId;
              if (embeddedData.wabaId) payload.wabaId = embeddedData.wabaId;
              if (embeddedData.businessAccountId) payload.businessAccountId = embeddedData.businessAccountId;
              if (embeddedData.businessName) payload.businessName = embeddedData.businessName;
              if (embeddedData.displayPhoneNumber) payload.displayPhoneNumber = embeddedData.displayPhoneNumber;

              const { data } = await api.post('/whatsapp/connect', payload);

              setWhatsAppIntegration(data);
              setWhatsAppFeedback({
                type: 'success',
                message: `WhatsApp conectado com sucesso${data.displayPhoneNumber ? `: ${data.displayPhoneNumber}` : '.'}`,
              });
              resolve();
            } catch (requestError: any) {
              reject(new Error(requestError?.response?.data?.message || 'Falha ao salvar a integracao do WhatsApp.'));
            }
          })();
        };

        const closeWatcher = window.setInterval(() => {
          if (popup.closed) {
            cleanup();
            reject(new Error('A janela do Embedded Signup foi fechada antes da conclusao.'));
          }
        }, 500);

        window.addEventListener('message', handleOAuthMessage);
      });
    } catch (error: any) {
      setWhatsAppFeedback({
        type: 'error',
        message: error?.message || 'Nao foi possivel conectar o WhatsApp Business.',
      });
    } finally {
      setWhatsAppConnecting(false);
    }
  };

  const disconnectWhatsApp = async () => {
    try {
      setWhatsAppDisconnecting(true);
      setWhatsAppFeedback(null);
      const { data } = await api.delete('/whatsapp/integration');
      setWhatsAppIntegration(data);
      setWhatsAppFeedback({
        type: 'success',
        message: 'Integracao do WhatsApp desconectada.',
      });
    } catch (error: any) {
      setWhatsAppFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Nao foi possivel desconectar o WhatsApp.',
      });
    } finally {
      setWhatsAppDisconnecting(false);
    }
  };

  const testWhatsAppConnection = async () => {
    try {
      setWhatsAppTesting(true);
      setWhatsAppFeedback(null);
      await api.post('/whatsapp/test-message', {
        to: whatsAppTestForm.to,
        text: whatsAppTestForm.text,
      });
      setWhatsAppFeedback({
        type: 'success',
        message: 'Mensagem de teste enviada com sucesso pelo numero conectado do restaurante.',
      });
    } catch (error: any) {
      setWhatsAppFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Nao foi possivel enviar a mensagem de teste.',
      });
    } finally {
      setWhatsAppTesting(false);
    }
  };

  const refreshWhatsAppInbox = async () => {
    try {
      setWhatsAppInboxLoading(true);
      const { data } = await api.get('/whatsapp/message-logs?limit=12');
      setWhatsAppMessageLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to refresh WhatsApp message logs', error);
    } finally {
      setWhatsAppInboxLoading(false);
    }
  };

  const saveSettings = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.establishmentId) return;

    try {
      setSaving(true);
      if (activeSection === 'entrega') {
        setDeliveryZoneSaving(true);
        setDeliveryZoneFeedback(null);
      }
      let logoUrl = form.logoUrl || undefined;
      let coverImageUrl = form.coverImageUrl || undefined;
      let coverImageKey = form.coverImageKey || undefined;
      let shareImageUrl = form.shareImageUrl || undefined;
      let shareImageKey = form.shareImageKey || undefined;

      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        const uploadResponse = await api.post(`/establishments/${user.establishmentId}/upload-logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        logoUrl = uploadResponse.data.logoUrl;
      }

      if (coverImageFile) {
        const formData = new FormData();
        formData.append('file', coverImageFile);
        const uploadResponse = await api.post(`/establishments/${user.establishmentId}/upload-cover-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        coverImageUrl = uploadResponse.data.coverImageUrl;
        coverImageKey = uploadResponse.data.coverImageKey;
      }

      if (shareImageFile) {
        const formData = new FormData();
        formData.append('file', shareImageFile);
        const uploadResponse = await api.post(`/establishments/${user.establishmentId}/upload-share-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        shareImageUrl = uploadResponse.data.shareImageUrl;
        shareImageKey = uploadResponse.data.shareImageKey;
      }

      await api.patch(`/establishments/${user.establishmentId}`, {
        name: form.name,
        address: form.address,
        phone: form.phone,
        logoUrl,
        settings: {
          currency: form.currency,
          theme: form.theme,
          deliveryPaymentMethods: form.deliveryPaymentMethods,
          deliveryEnabled: form.deliveryEnabled,
          pickupEnabled: form.pickupEnabled,
          deliveryFixedFee: Math.max(0, Number(form.deliveryFixedFee) || 0),
          deliveryMapCenterLat: Number(form.deliveryMapCenterLat) || -19.9167,
          deliveryMapCenterLng: Number(form.deliveryMapCenterLng) || -43.9345,
          hasReservations: form.hasReservations,
          reservationSlotMinutes: Number(form.reservationSlotMinutes) || 30,
          maxReservationsPerSlot: Number(form.maxReservationsPerSlot) || 4,
          maxGuestsPerSlot: Number(form.maxGuestsPerSlot) || 16,
          operatingHours: form.operatingHours,
          coverImageUrl,
          coverImageKey,
          shareImageUrl,
          shareImageKey,
          tagline: form.tagline,
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          storeThemePreset: form.storeThemePreset,
        },
      });

      if (activeSection === 'entrega' && user?.establishmentId) {
        const persistedIds = new Set(persistedDeliveryZones.map((zone) => zone.id));
        const currentIds = new Set(deliveryZones.filter((zone) => !zone.id.startsWith('temp_')).map((zone) => zone.id));

        const zonesToDelete = persistedDeliveryZones.filter((zone) => !currentIds.has(zone.id));
        const zonesToCreate = deliveryZones.filter((zone) => zone.id.startsWith('temp_'));
        const zonesToUpdate = deliveryZones.filter((zone) => !zone.id.startsWith('temp_'));

        await Promise.all(zonesToDelete.map((zone) => api.delete(`/delivery-zones/${zone.id}`)));
        await Promise.all(zonesToCreate.map((zone) => api.post(`/establishments/${user.establishmentId}/delivery-zones`, {
          name: zone.name,
          geojson: zone.geojson,
          deliveryFee: Math.max(0, Number(zone.deliveryFee) || 0),
          isActive: zone.isActive,
        })));
        await Promise.all(zonesToUpdate
          .filter((zone) => persistedIds.has(zone.id))
          .map((zone) => api.patch(`/delivery-zones/${zone.id}`, {
            name: zone.name,
            geojson: zone.geojson,
            deliveryFee: Math.max(0, Number(zone.deliveryFee) || 0),
            isActive: zone.isActive,
          })));

        const { data: freshEstablishment } = await api.get(`/establishments/id/${user.establishmentId}`);
        setDeliveryZones(freshEstablishment.deliveryZones ?? []);
        setPersistedDeliveryZones(freshEstablishment.deliveryZones ?? []);
        setDeliveryZoneFeedback({
          type: 'success',
          message: 'Area de entrega salva com sucesso. O checkout de delivery ja usa os poligonos publicados.',
        });
      }

      setForm((prev) => ({
        ...prev,
        logoUrl: logoUrl ?? '',
        coverImageUrl: coverImageUrl ?? '',
        coverImageKey: coverImageKey ?? '',
        shareImageUrl: shareImageUrl ?? '',
        shareImageKey: shareImageKey ?? '',
      }));
      setLogoFile(null);
      setCoverImageFile(null);
      setShareImageFile(null);
    } catch (error) {
      console.error('Failed to save settings', error);
      if (activeSection === 'entrega') {
        setDeliveryZoneFeedback({
          type: 'error',
          message: 'Nao foi possivel salvar a area de entrega. Revise os poligonos e tente novamente.',
        });
      }
    } finally {
      setSaving(false);
      setDeliveryZoneSaving(false);
    }
  };

  const openCheckout = async () => {
    if (!user?.establishmentId) return;
    try {
      setRedirectingBilling(true);
      setBillingFeedback(null);
      const { data } = await api.post('/billing/checkout-session', {
        establishmentId: user.establishmentId,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setBillingFeedback({
        type: 'error',
        message: 'O Stripe nao retornou a URL de pagamento. Revise a configuracao do plano e tente novamente.',
      });
    } catch (error: any) {
      console.error('Failed to create Stripe checkout session', error);
      setBillingFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Nao foi possivel abrir o pagamento da assinatura.',
      });
    } finally {
      setRedirectingBilling(false);
    }
  };

  const openPortal = async () => {
    if (!user?.establishmentId) return;
    try {
      setRedirectingBilling(true);
      setBillingFeedback(null);
      const { data } = await api.post('/billing/portal-session', {
        establishmentId: user.establishmentId,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setBillingFeedback({
        type: 'error',
        message: 'O Stripe nao retornou a URL do portal do cliente.',
      });
    } catch (error: any) {
      console.error('Failed to create Stripe portal session', error);
      setBillingFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Nao foi possivel abrir o portal da assinatura.',
      });
    } finally {
      setRedirectingBilling(false);
    }
  };

  const saveMercadoPagoCredentials = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.establishmentId) return;

    try {
      setPaymentSaving(true);
      setPaymentFeedback(null);

      const { data } = await api.patch(`/establishments/${user.establishmentId}/payment-settings/mercado-pago`, {
        mpPublicKey: mercadoPagoForm.mpPublicKey,
        mpAccessToken: mercadoPagoForm.mpAccessToken || undefined,
      });

      setMercadoPagoForm((prev) => ({
        ...prev,
        mpPublicKey: data.mpPublicKey ?? prev.mpPublicKey,
        mpAccessToken: '',
        hasAccessToken: !!data.hasAccessToken,
        updatedAt: data.updatedAt ?? '',
      }));
      setPaymentFeedback({
        type: 'success',
        message: 'Credenciais do Mercado Pago salvas com sucesso.',
      });
    } catch (error: any) {
      setPaymentFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Nao foi possivel salvar as credenciais.',
      });
    } finally {
      setPaymentSaving(false);
    }
  };

  const testMercadoPagoConnection = async () => {
    if (!user?.establishmentId) return;

    try {
      setPaymentTesting(true);
      setPaymentFeedback(null);

      const { data } = await api.post(`/establishments/${user.establishmentId}/payment-settings/mercado-pago/test`, {
        mpPublicKey: mercadoPagoForm.mpPublicKey || undefined,
        mpAccessToken: mercadoPagoForm.mpAccessToken || undefined,
      });

      const label = data?.account?.nickname || data?.account?.email || 'Conta validada';
      setPaymentFeedback({
        type: 'success',
        message: `Conexao validada com sucesso: ${label}.`,
      });
    } catch (error: any) {
      setPaymentFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Falha ao validar as credenciais do Mercado Pago.',
      });
    } finally {
      setPaymentTesting(false);
    }
  };

  const createTable = async () => {
    if (!user?.establishmentId) return;

    try {
      setTableSaving(true);
      const { data } = await api.post(`/establishments/${user.establishmentId}/tables`, {
        label: tableDraft.label,
        capacity: Number(tableDraft.capacity),
      });
      setTables((prev) => [...prev, data].sort((a, b) => a.label.localeCompare(b.label)));
      setTableDraft({ label: '', capacity: '4' });
    } catch (error) {
      console.error('Failed to create table', error);
    } finally {
      setTableSaving(false);
    }
  };

  const updateTable = async (tableId: string, updates: { label?: string; capacity?: number; active?: boolean }) => {
    try {
      const { data } = await api.patch(`/establishments/tables/${tableId}`, updates);
      setTables((prev) => prev.map((table) => (table.id === tableId ? data : table)));
    } catch (error) {
      console.error('Failed to update table', error);
    }
  };

  const deleteTable = async (tableId: string) => {
    try {
      await api.post(`/establishments/tables/${tableId}/delete`);
      setTables((prev) => prev.filter((table) => table.id !== tableId));
    } catch (error) {
      console.error('Failed to delete table', error);
    }
  };

  const [activeSection, setActiveSection] = useState<'geral' | 'entrega' | 'reservas' | 'mesas' | 'pagamentos' | 'integracoes'>('geral');

  const sharePreviewUrl = (() => {
    const apiBaseUrl = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
    return form.slug ? `${apiBaseUrl}/establishments/share/${form.slug}` : '';
  })();

  const copySharePreviewLink = async () => {
    if (!sharePreviewUrl) return;

    try {
      await navigator.clipboard.writeText(sharePreviewUrl);
      setCopiedShareLink(true);
      window.setTimeout(() => setCopiedShareLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy store share preview link', error);
    }
  };

  const settingsSections = [
    {
      id: 'geral' as const,
      label: 'Dados gerais',
      description: 'Identificacao do restaurante, assinatura e checkout.',
      icon: Settings,
      visible: true,
    },
    {
      id: 'entrega' as const,
      label: 'Area de entrega',
      description: 'Delivery, retirada no local e poligonos de cobertura no mapa.',
      icon: Truck,
      visible: form.deliveryEnabled || form.pickupEnabled,
    },
    {
      id: 'reservas' as const,
      label: 'Reservas e agenda',
      description: 'Horarios, disponibilidade e capacidade operacional.',
      icon: CalendarClock,
      visible: reservationModuleEnabled,
    },
    {
      id: 'mesas' as const,
      label: 'Mesas do salao',
      description: 'Mapa fisico, cadeiras e ocupacao por mesa.',
      icon: CalendarClock,
      visible: tableModuleEnabled,
    },
    {
      id: 'pagamentos' as const,
      label: 'Pagamentos',
      description: 'Credenciais do Mercado Pago e cobranca online.',
      icon: CreditCard,
      visible: form.deliveryEnabled || form.pickupEnabled,
    },
    {
      id: 'integracoes' as const,
      label: 'Integracoes',
      description: 'WhatsApp Business e canais externos do tenant.',
      icon: MessageCircleMore,
      visible: true,
    },
  ].filter((section) => section.visible);

  const settingsSectionGroups = [
    {
      title: 'Negocio',
      items: ['geral'],
    },
    {
      title: 'Operacao',
      items: ['entrega', 'reservas', 'mesas'],
    },
    {
      title: 'Cobranca e canais',
      items: ['pagamentos', 'integracoes'],
    },
  ];

  const visibleSettingsSectionGroups = settingsSectionGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((sectionId) => settingsSections.some((section) => section.id === sectionId)),
    }))
    .filter((group) => group.items.length > 0);

  const whatsAppIsConfigured = Boolean(whatsAppPlatformConfig?.enabled && whatsAppPlatformConfig?.appId && whatsAppPlatformConfig?.configId);
  const whatsAppIsConnected = Boolean(whatsAppIntegration?.connected);
  const whatsAppHasInboundTest = whatsAppMessageLogs.some((log) => log.direction === 'INBOUND');
  const whatsAppHasOutboundTest = whatsAppMessageLogs.some((log) => log.direction === 'OUTBOUND' && log.status !== 'error');
  const whatsAppWizardSteps = [
    {
      title: 'Preparar numero',
      status: 'manual',
      description: 'Tenha acesso ao Facebook do dono, ao SMS/ligacao do numero e confirme que ele nao esta ativo em outro WhatsApp.',
    },
    {
      title: 'Conectar com a Meta',
      status: whatsAppIsConnected ? 'done' : whatsAppIsConfigured ? 'current' : 'blocked',
      description: whatsAppIsConfigured
        ? 'Use o Embedded Signup oficial para autorizar o Garcom Digital a operar o numero pela Cloud API.'
        : 'A configuracao da Meta ainda nao esta completa no backend.',
    },
    {
      title: 'Validar envio',
      status: whatsAppHasOutboundTest ? 'done' : whatsAppIsConnected ? 'current' : 'blocked',
      description: 'Envie uma mensagem de teste para confirmar que o token e o template/texto estao funcionando.',
    },
    {
      title: 'Validar recebimento',
      status: whatsAppHasInboundTest ? 'done' : whatsAppIsConnected ? 'current' : 'blocked',
      description: 'Envie uma mensagem para o numero conectado e confirme se o webhook registrou a entrada.',
    },
  ];
  const whatsAppManualMessage = `Ola! Sou do ${form.name || 'restaurante'} e estou atendendo pelo Garcom Digital.`;
  const whatsAppManualLink = whatsAppTestForm.to
    ? `https://wa.me/${whatsAppTestForm.to.replace(/\D/g, '')}?text=${encodeURIComponent(whatsAppManualMessage)}`
    : '';

  useEffect(() => {
    if (!settingsSections.some((section) => section.id === activeSection)) {
      setActiveSection('geral');
    }
  }, [activeSection, settingsSections]);

  const activeSectionMeta = settingsSections.find((section) => section.id === activeSection);

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground">Carregando configuracoes...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Configuracoes</h1>
        <p className="mt-1 text-sm text-muted-foreground">Dados do estabelecimento, preferencias e informacoes da assinatura.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="hidden xl:block xl:sticky xl:top-24">
          <div className="card h-fit p-4">
          <div className="border-b border-[#2A2A2A] pb-4">
            <h2 className="text-lg font-bold text-white">Configuracoes</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Navegue por modulos separados, com um menu lateral fixo e uma estrutura mais proxima de paineis densos.
            </p>
          </div>
          <nav className="mt-4 space-y-5">
            {visibleSettingsSectionGroups.map((group) => (
              <div key={group.title} className="space-y-2">
                <p className="px-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground/80">{group.title}</p>
                <div className="space-y-1">
                  {group.items.map((sectionId) => {
                    const section = settingsSections.find((item) => item.id === sectionId);
                    if (!section) return null;

                    const isActive = activeSection === section.id;

                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => setActiveSection(section.id)}
                        className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
                          isActive
                            ? 'border-primary/40 bg-primary/10 text-white shadow-[0_0_0_1px_rgba(249,115,22,0.1)]'
                            : 'border-transparent bg-[#111111] text-muted-foreground hover:border-[#2A2A2A] hover:text-white'
                        }`}
                      >
                        <section.icon className={`mt-0.5 h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                        <div>
                          <p className="text-sm font-semibold">{section.label}</p>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">{section.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="mt-5 rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Em foco</p>
            <p className="mt-2 text-sm font-semibold text-white">{activeSectionMeta?.label}</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{activeSectionMeta?.description}</p>
          </div>
          </div>
        </aside>

        <div className="space-y-6">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#101010] p-3">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
              {settingsSections.map((section) => {
                const isActive = activeSection === section.id;

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSection(section.id)}
                    className={`whitespace-nowrap rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'border-primary/40 bg-primary/10 text-white'
                        : 'border-[#2A2A2A] bg-[#151515] text-muted-foreground hover:border-primary/25 hover:text-white'
                    }`}
                  >
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card border-[#2A2A2A] bg-[#101010] p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">Painel de configuracao</p>
                <h2 className="mt-2 text-2xl font-black text-white">{activeSectionMeta?.label}</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">{activeSectionMeta?.description}</p>
              </div>
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-muted-foreground">
                Ajuste apenas este modulo. Os demais ficam separados no menu lateral.
              </div>
            </div>
          </div>

      {activeSection === 'geral' && subscription && (
        <div className="card w-full p-6">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Assinatura atual</h2>
              <p className="mt-1 text-sm text-muted-foreground">Gerenciada pela plataforma. Aqui o tenant so visualiza o plano.</p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Plano</p>
              <p className="mt-2 text-lg font-bold text-white">{subscriptionLabel[subscription.subscriptionPlan] ?? subscription.subscriptionPlan}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
              <p className="mt-2 text-lg font-bold text-white">{statusLabel[subscription.subscriptionStatus] ?? subscription.subscriptionStatus}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Ciclo</p>
              <p className="mt-2 text-lg font-bold text-white">{cycleLabel[subscription.billingCycle] ?? subscription.billingCycle}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valor</p>
              <p className="mt-2 text-lg font-bold text-primary">{formatPrice(subscription.subscriptionPrice ?? 0)}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Fim do trial</p>
              <p className="mt-2 text-lg font-bold text-white">{subscription.trialEndsAt ? new Date(subscription.trialEndsAt).toLocaleDateString('pt-BR') : 'Nao definido'}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Proxima cobranca</p>
              <p className="mt-2 text-lg font-bold text-white">{subscription.nextBillingAt ? new Date(subscription.nextBillingAt).toLocaleDateString('pt-BR') : 'Nao definido'}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            {billingFeedback && (
              <div
                className={`w-full rounded-2xl border p-4 text-sm ${
                  billingFeedback.type === 'success'
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                    : 'border-red-500/30 bg-red-500/10 text-red-200'
                }`}
              >
                {billingFeedback.message}
              </div>
            )}
            <button type="button" onClick={openCheckout} disabled={redirectingBilling} className="btn-primary flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {redirectingBilling ? 'Abrindo...' : 'Pagar'}
            </button>
            <button type="button" onClick={openPortal} disabled={redirectingBilling} className="rounded-xl border border-[#2A2A2A] px-4 py-2 text-sm font-medium text-white">
              <ExternalLink className="mr-2 inline h-4 w-4" />
              Portal do cliente
            </button>
          </div>
        </div>
      )}

      {(activeSection === 'geral' || activeSection === 'entrega' || activeSection === 'reservas') && (
      <form onSubmit={saveSettings} className="w-full max-w-none space-y-6">
        {activeSection === 'geral' && (
        <>
        <div id="secao-geral" className="card scroll-mt-24 space-y-6 p-6">
          <div>
            <h2 className="text-lg font-bold text-white">Dados do restaurante</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Informacoes basicas da operacao. Esses dados aparecem no painel e ajudam a identificar o restaurante no sistema.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-white">Nome do estabelecimento</label>
              <input value={form.name} onChange={(e) => updateField('name', e.target.value)} className="input" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-white">Endereco</label>
              <input value={form.address} onChange={(e) => updateField('address', e.target.value)} className="input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Telefone</label>
              <input value={form.phone} onChange={(e) => updateField('phone', e.target.value)} className="input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                className="input file:mr-3 file:border-0 file:bg-primary file:px-3 file:py-2 file:text-white file:rounded-lg"
              />
              {form.logoUrl && (
                <div className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                  <img src={resolveAssetUrl(form.logoUrl)} alt="Logo atual" className="h-12 w-12 rounded-xl object-cover" />
                  <span className="text-sm text-muted-foreground">Logo atual carregada do storage.</span>
                </div>
              )}
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-white">Imagem de capa da loja</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCoverImageFile(e.target.files?.[0] ?? null)}
                className="input file:mr-3 file:border-0 file:bg-primary file:px-3 file:py-2 file:text-white file:rounded-lg"
              />
              <p className="text-xs text-muted-foreground">
                Essa capa aparece no topo do cardapio publico e ajuda a dar uma identidade propria para o restaurante.
              </p>
              {(form.coverImageUrl || form.logoUrl) && (
                <div className="overflow-hidden rounded-2xl border border-[#2A2A2A] bg-[#151515]">
                  <div className="aspect-[3/1] w-full bg-[#101010]">
                    <img
                      src={resolveAssetUrl(form.coverImageUrl || form.logoUrl)}
                      alt="Capa atual da loja"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-white">Imagem da previa do link</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setShareImageFile(e.target.files?.[0] ?? null)}
                className="input file:mr-3 file:border-0 file:bg-primary file:px-3 file:py-2 file:text-white file:rounded-lg"
              />
              <p className="text-xs text-muted-foreground">
                Essa imagem aparece na previa quando voce enviar o link especial do restaurante no WhatsApp, Instagram, Facebook e apps semelhantes.
              </p>
              {(form.shareImageUrl || form.logoUrl) && (
                <div className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                  <img
                    src={resolveAssetUrl(form.shareImageUrl || form.logoUrl)}
                    alt="Imagem atual da previa"
                    className="h-16 w-24 rounded-xl object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white">
                      {form.shareImageUrl ? 'Imagem de previa configurada.' : 'Usando a logo como fallback da previa.'}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground break-all">
                      {sharePreviewUrl || 'Link de compartilhamento indisponivel enquanto o slug nao existir.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={copySharePreviewLink}
                    disabled={!sharePreviewUrl}
                    className="rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm font-semibold text-white transition hover:border-primary/50 disabled:opacity-50"
                  >
                    <Copy className="mr-2 inline h-4 w-4" />
                    {copiedShareLink ? 'Copiado!' : 'Copiar link'}
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-white">Tagline da loja</label>
              <input
                value={form.tagline}
                onChange={(e) => updateField('tagline', e.target.value)}
                className="input"
                placeholder="Ex.: Burgers artesanais, chop gelado e entrega rapida."
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Preset visual</label>
              <select
                value={form.storeThemePreset}
                onChange={(e) => updateField('storeThemePreset', e.target.value)}
                className="input appearance-none bg-[#1A1A1A]"
              >
                <option value="classic">Classico</option>
                <option value="urban">Urbano</option>
                <option value="bistro">Bistro</option>
                <option value="fresh">Fresh</option>
              </select>
            </div>
            <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Cor principal</label>
                <div className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#151515] px-3 py-2">
                  <input
                    type="color"
                    value={form.primaryColor}
                    onChange={(e) => updateField('primaryColor', e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                  />
                  <input
                    value={form.primaryColor}
                    onChange={(e) => updateField('primaryColor', e.target.value)}
                    className="input h-10 flex-1 border-0 bg-transparent px-0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Cor de apoio</label>
                <div className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#151515] px-3 py-2">
                  <input
                    type="color"
                    value={form.accentColor}
                    onChange={(e) => updateField('accentColor', e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                  />
                  <input
                    value={form.accentColor}
                    onChange={(e) => updateField('accentColor', e.target.value)}
                    className="input h-10 flex-1 border-0 bg-transparent px-0"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Moeda</label>
              <select value={form.currency} onChange={(e) => updateField('currency', e.target.value)} className="input appearance-none bg-[#1A1A1A]">
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Tema</label>
              <select value={form.theme} onChange={(e) => updateField('theme', e.target.value)} className="input appearance-none bg-[#1A1A1A]">
                <option value="dark">Escuro</option>
                <option value="light">Claro</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card space-y-6 p-6">
          <div>
            <h2 className="text-lg font-bold text-white">Delivery e checkout</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Escolha quais formas de pagamento o cliente pode usar quando fizer um pedido delivery.
            </p>
          </div>
          <div className="space-y-3">
            <label className="text-sm font-medium text-white">Pagamentos habilitados no delivery</label>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { value: 'MERCADO_PAGO', label: 'Pagar agora com Mercado Pago' },
                { value: 'CARD_ON_DELIVERY', label: 'Cartao na entrega' },
                { value: 'CASH', label: 'Dinheiro' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleDeliveryPaymentMethod(option.value)}
                  className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                    form.deliveryPaymentMethods.includes(option.value)
                      ? 'border-primary bg-primary/15 text-white'
                      : 'border-[#2A2A2A] bg-[#151515] text-muted-foreground'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Esses metodos aparecem apenas no checkout de pedidos delivery. O restante da operacao nao depende dessa escolha.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">Taxa padrao de entrega</label>
            <div className="relative max-w-xs">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">R$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.deliveryFixedFee}
                onChange={(e) => updateField('deliveryFixedFee', e.target.value)}
                className="input pl-12"
                placeholder="0,00"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Esse valor funciona como fallback quando a area de entrega nao tiver uma taxa propria configurada.
            </p>
          </div>
        </div>
        </>
        )}

        {activeSection === 'entrega' && (
          <DeliverySettingsSection
            form={form}
            deliveryZones={deliveryZones}
            deviceLocationLoading={deviceLocationLoading}
            addressLocationLoading={addressLocationLoading}
            deviceLocationError={deviceLocationError}
            deliveryZoneFeedback={deliveryZoneFeedback}
            updateToggleField={updateToggleField}
            updateDeliveryZone={updateDeliveryZone}
            useDeviceLocationForDeliveryMap={useDeviceLocationForDeliveryMap}
            centerDeliveryMapFromRestaurantAddress={centerDeliveryMapFromRestaurantAddress}
            setDeliveryZones={setDeliveryZones}
            setDeliveryZoneFeedback={setDeliveryZoneFeedback}
          />
        )}

        {activeSection === 'reservas' && (
          <ReservationsSettingsSection
            reservationModuleEnabled={reservationModuleEnabled}
            form={form}
            updateBooleanField={updateBooleanField}
            updateField={updateField}
            updateOperatingHour={updateOperatingHour}
          />
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            <Save className="h-4 w-4" />
            {saving || deliveryZoneSaving
              ? 'Salvando...'
              : activeSection === 'geral'
                ? 'Salvar configuracoes gerais'
                : activeSection === 'entrega'
                  ? 'Salvar area de entrega'
                  : 'Salvar regras de reservas'}
          </button>
        </div>
      </form>
      )}

      {activeSection === 'mesas' && (
        <TablesSettingsSection
          tableModuleEnabled={tableModuleEnabled}
          tables={tables}
          tableDraft={tableDraft}
          tableSaving={tableSaving}
          setTables={setTables}
          setTableDraft={setTableDraft}
          createTable={createTable}
          updateTable={updateTable}
          deleteTable={deleteTable}
        />
      )}

      {activeSection === 'pagamentos' && (
        <PaymentsSettingsSection
          mercadoPagoForm={mercadoPagoForm}
          paymentFeedback={paymentFeedback}
          paymentSaving={paymentSaving}
          paymentTesting={paymentTesting}
          updateMercadoPagoField={updateMercadoPagoField}
          saveMercadoPagoCredentials={saveMercadoPagoCredentials}
          testMercadoPagoConnection={testMercadoPagoConnection}
        />
      )}

      {activeSection === 'integracoes' && (
        <WhatsAppSettingsSection
          whatsAppFeedback={whatsAppFeedback}
          whatsAppIsConnected={whatsAppIsConnected}
          whatsAppIsConfigured={whatsAppIsConfigured}
          whatsAppHasOutboundTest={whatsAppHasOutboundTest}
          whatsAppHasInboundTest={whatsAppHasInboundTest}
          whatsAppWizardSteps={whatsAppWizardSteps}
          whatsAppManualLink={whatsAppManualLink}
          whatsAppLoading={whatsAppLoading}
          whatsAppIntegration={whatsAppIntegration}
          whatsAppPlatformConfig={whatsAppPlatformConfig}
          whatsAppConnecting={whatsAppConnecting}
          whatsAppDisconnecting={whatsAppDisconnecting}
          whatsAppTesting={whatsAppTesting}
          whatsAppInboxLoading={whatsAppInboxLoading}
          whatsAppTestForm={whatsAppTestForm}
          whatsAppMessageLogs={whatsAppMessageLogs}
          setWhatsAppTestForm={setWhatsAppTestForm}
          openWhatsAppEmbeddedSignup={openWhatsAppEmbeddedSignup}
          disconnectWhatsApp={disconnectWhatsApp}
          testWhatsAppConnection={testWhatsAppConnection}
          refreshWhatsAppInbox={refreshWhatsAppInbox}
        />
      )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
